// EmptyState — the one shared empty-state pattern (design-system 13-patterns,
// EMPTY-STATE LAW).
//
// One pattern across the whole app: a quiet dashed panel, an optional icon, a
// full-contrast title, an italic muted body in Greenhold's no-noise voice, and
// at most one ghost action. Never an illustration, never a bare "no rows".
//
// Surface-neutral by design. The app has both dark (forest-900) and light
// (paper) surfaces and does NOT toggle the `.dark` class, so this component
// sets NO explicit text colour: the title inherits the surrounding
// currentColor (full contrast on either ground) and the body/icon are muted
// with `opacity` rather than a fixed colour. The panel uses the adaptive
// `border`/`card` semantic tokens, which read as a subtle frosted panel on
// dark grounds and a quiet outline on paper — matching the treatment the
// pages already used ad-hoc.
//
// Usage:
//   <EmptyState
//     title="No listings yet"
//     description="Greenhold doesn't manufacture noise — check back after Friday's drop."
//     action={<Button variant="ghost">Browse the shop</Button>}
//   />

import type { ReactNode } from "react";

export interface EmptyStateProps {
  /** Optional leading glyph/icon rendered above the title. */
  icon?: ReactNode;
  /** Full-contrast headline. Optional — body-only empty states are valid. */
  title?: ReactNode;
  /** Italic, muted body copy in the no-noise voice. */
  description?: ReactNode;
  /** At most one ghost action (a Button/Link). */
  action?: ReactNode;
  /** Accessible label for the region (e.g. "Empty vault"). */
  "aria-label"?: string;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  "aria-label": ariaLabel,
  className,
}: EmptyStateProps) {
  return (
    <section
      aria-label={ariaLabel}
      data-testid="empty-state"
      className={[
        "flex flex-col items-center gap-3 rounded-lg border border-dashed border-border/70 bg-card/30 px-6 py-12 text-center",
        className ?? "",
      ]
        .join(" ")
        .trim()}
    >
      {icon ? (
        <div className="opacity-60" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      {title ? (
        <p className="font-heading text-base font-semibold">{title}</p>
      ) : null}
      {description ? (
        <p className="max-w-md font-body text-sm italic opacity-70">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </section>
  );
}
